import { motion } from "framer-motion";

const models = [
  {
    n: "01",
    title: "Fractional CPO / CPSO",
    cadence: "Monthly Retainer",
    body: "Embedded product leadership for companies that need senior product judgment in the room — without a full-time hire.",
  },
  {
    n: "02",
    title: "AI & Strategic Product Advisory",
    cadence: "Ongoing Engagement",
    body: "A direct line to the CEO. Senior counsel on the highest-stakes product, AI and growth decisions — month after month.",
  },
  {
    n: "03",
    title: "Strategic Product Interventions",
    cadence: "Project / Sprint",
    body: "Time-boxed engagements: product audits, roadmap resets, AI opportunity mapping and transformation playbooks.",
  },
];

const goals = [
  "Build or scale an AI-native product",
  "Make the right product bets — fast",
  "Align product with growth and valuation",
];

export const Engage = () => {
  return (
    <section
      id="engage"
      data-testid="engage-section"
      className="relative bg-[#050505] py-20 sm:py-28 md:py-40 border-t border-[#141414]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-8 mb-12 md:mb-16 md:items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A880]" />
              <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880]">
                Work With Me / 08
              </span>
            </div>
            <h2
              data-testid="engage-heading"
              className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] lg:leading-[1.05] tracking-tight text-[#F5F5F0]"
            >
              Three ways to engage —{" "}
              <em className="italic text-[#C5A880]">shaped to your stage</em>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#C5A880] mb-4">
              You're here to —
            </p>
            <ul className="space-y-2">
              {goals.map((g) => (
                <li
                  key={g}
                  className="flex items-start gap-3 text-[#F5F5F0] text-[15px] sm:text-base md:text-lg leading-relaxed"
                >
                  <span className="mt-2 h-px w-4 bg-[#C5A880] flex-shrink-0 inline-block" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border border-[#1a1a1a]">
          {models.map((m, i) => (
            <motion.div
              key={m.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`engage-model-${m.n}`}
              className={`p-6 sm:p-8 md:p-12 ${
                i < models.length - 1 ? "md:border-r border-[#1a1a1a]" : ""
              } ${i === 0 || i === 1 ? "border-b md:border-b-0 border-[#1a1a1a]" : ""} hover:bg-[#0b0b0b] transition-colors min-h-[260px] sm:min-h-[320px] flex flex-col`}
            >
              <div className="flex items-center justify-between mb-8 sm:mb-10 gap-2">
                <span className="text-[11px] tracking-[0.3em] text-[#C5A880]">
                  Model {m.n}
                </span>
                <span className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-[#A3A39A] text-right">
                  {m.cadence}
                </span>
              </div>
              <h3 className="font-serif-display text-2xl sm:text-3xl md:text-4xl font-light text-[#F5F5F0] mb-4 sm:mb-6 leading-tight">
                {m.title}
              </h3>
              <p className="text-[#A3A39A] text-[15px] sm:text-base leading-relaxed mt-auto">
                {m.body}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 sm:mt-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 lg:gap-12 border-t border-[#1a1a1a] pt-8 sm:pt-10">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880] mb-5 sm:mb-6">
              Clients choose to work with me when they need —
            </p>
            <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <li className="flex items-start gap-4 font-serif-display text-xl sm:text-2xl md:text-3xl font-light text-[#F5F5F0] leading-tight">
                <span className="text-[#C5A880] text-sm tracking-widest mt-2 sm:mt-3 flex-shrink-0">01</span>
                <span><span className="italic text-[#C5A880]">Clarity</span> over complexity</span>
              </li>
              <li className="flex items-start gap-4 font-serif-display text-xl sm:text-2xl md:text-3xl font-light text-[#F5F5F0] leading-tight">
                <span className="text-[#C5A880] text-sm tracking-widest mt-2 sm:mt-3 flex-shrink-0">02</span>
                <span>Strategic direction — not just execution</span>
              </li>
              <li className="flex items-start gap-4 font-serif-display text-xl sm:text-2xl md:text-3xl font-light text-[#F5F5F0] leading-tight">
                <span className="text-[#C5A880] text-sm tracking-widest mt-2 sm:mt-3 flex-shrink-0">03</span>
                <span>A partner fluent in both technology and the realities of the built environment</span>
              </li>
            </ul>
            <p className="text-[15px] sm:text-base md:text-lg text-[#A3A39A] leading-relaxed italic">
              This isn't just what I do — it's the kind of problem space I've
              deliberately chosen to operate in.
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:items-end lg:flex-shrink-0">
            <a
              href="mailto:hello@denisantony.com"
              data-testid="engage-cta"
              className="group inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-4 min-h-[48px] bg-[#C5A880] text-[#050505] text-[12px] font-semibold tracking-[0.25em] uppercase hover:bg-[#D8BE96] transition-colors w-full lg:w-auto"
            >
              Start The Conversation
              <span className="transition-transform duration-300 group-hover:translate-x-0.5">→</span>
            </a>
            <p className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-[#666] lg:text-right">
              Direct response · Within two business days
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Engage;
